import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  AlertTriangle,
  Circle,
  Download,
  Image as ImageIcon,
  Loader2,
  Lock,
  Map as MapIcon,
  Maximize,
  Spline,
  Square,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ORTO_WMS } from "@/components/map/gugik";

import {
  extentToBounds,
  featureInfoUrl,
  parseFeatureInfo,
  previewErrorMessage,
  type FeatureInfoHit,
} from "../gisPreview";
import { useGisPreview } from "../hooks";
import type { GisLayerKind, GisPreview } from "../types";

type Basemap = "osm" | "orto" | "none";

const BASEMAPS: { value: Basemap; label: string }[] = [
  { value: "osm", label: "OSM" },
  { value: "orto", label: "Orto" },
  { value: "none", label: "Brak" },
];

const KIND: Record<GisLayerKind, { icon: ReactNode; label: string }> = {
  raster: { icon: <ImageIcon size={13} />, label: "Raster" },
  polygon: { icon: <Square size={13} />, label: "Poligony" },
  line: { icon: <Spline size={13} />, label: "Linie" },
  point: { icon: <Circle size={13} />, label: "Punkty" },
};

interface IdentifyState {
  loading: boolean;
  failed: boolean;
  hits: FeatureInfoHit[];
}

interface Props {
  nodeId: string;
  name: string;
  onClose: () => void;
  onDownload: () => void;
}

/**
 * Full-screen, read-only map of a GeoPackage from the drive. QGIS Server renders the
 * file as WMS straight from where it is stored (no copy); the browser only gets images,
 * so nothing here can change the file. Clicking the map asks QGIS for the attributes of
 * what lies under the cursor.
 */
export function GisPreviewScreen({ nodeId, name, onClose, onDownload }: Props) {
  const { data: preview, error, isLoading } = useGisPreview(nodeId);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Above every page layer, like the document editor (see OdtEditorScreen).
  return createPortal(
    <div
      className="fixed inset-0 z-[1300] flex flex-col bg-[var(--color-bg-base)]"
      data-modal-layer
      data-testid="gis-preview-screen"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
        <MapIcon size={16} className="text-[var(--color-primary)] shrink-0" />
        <h2 className="text-sm font-medium truncate" title={name}>
          Podgląd mapy: {name}
        </h2>
        <span className="hidden sm:inline-flex items-center gap-1 shrink-0 border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] font-mono uppercase text-[var(--color-text-muted)]">
          <Lock size={10} /> tylko odczyt
        </span>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <Button size="sm" variant="secondary" onClick={onDownload}>
            <Download size={13} className="mr-1" /> Pobierz
          </Button>
          <Button size="sm" variant="secondary" onClick={onClose}>
            <X size={13} className="mr-1" /> Zamknij
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center gap-2 text-sm text-[var(--color-text-muted)]">
          <Spinner size="sm" /> Przygotowuję podgląd mapy…
        </div>
      ) : error || !preview ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-lg space-y-3 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-4">
            <p className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              {previewErrorMessage(error)}
            </p>
            <Button size="sm" variant="secondary" onClick={onDownload}>
              <Download size={13} className="mr-1" /> Pobierz plik
            </Button>
          </div>
        </div>
      ) : (
        <GisPreviewBody preview={preview} />
      )}
    </div>,
    document.body,
  );
}

function GisPreviewBody({ preview }: { preview: GisPreview }) {
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [basemap, setBasemap] = useState<Basemap>("osm");
  const [identify, setIdentify] = useState<IdentifyState | null>(null);
  const [tilesLoading, setTilesLoading] = useState(false);

  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const baseRef = useRef<L.Layer | null>(null);
  const wmsRef = useRef<L.TileLayer.WMS | null>(null);
  const wmsLayersRef = useRef("");
  const markerRef = useRef<L.CircleMarker | null>(null);
  const identifySeq = useRef(0);

  // Draw order bottom → top, as the backend sends it — WMS paints LAYERS in list order.
  const visible = useMemo(
    () => preview.layers.filter((l) => !hidden.has(l.name)),
    [preview.layers, hidden],
  );
  const visibleNames = visible.map((l) => l.name).join(",");

  // The click handler is bound once, so it reads the current state through this ref.
  const latest = useRef({ preview, visible });
  latest.current = { preview, visible };

  const fitToExtent = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    if (preview.extent_3857) {
      map.fitBounds(extentToBounds(preview.extent_3857), { padding: [24, 24], maxZoom: 19 });
    } else {
      map.setView([52.1, 19.4], 6);
    }
  }, [preview.extent_3857]);

  const clearIdentify = useCallback(() => {
    identifySeq.current += 1;
    markerRef.current?.remove();
    markerRef.current = null;
    setIdentify(null);
  }, []);

  useEffect(() => {
    if (!mapEl.current) return;
    const map = L.map(mapEl.current, { maxZoom: 22 });
    mapRef.current = map;

    map.on("click", async (e: L.LeafletMouseEvent) => {
      const { preview: p, visible: layers } = latest.current;
      // Rasters answer with pixel values, not objects — query only the vector layers.
      const queryable = layers.filter((l) => l.kind !== "raster").map((l) => l.name);
      if (queryable.length === 0) return;
      markerRef.current?.remove();
      markerRef.current = L.circleMarker(e.latlng, {
        radius: 6,
        color: "#dc2626",
        weight: 2,
        fillOpacity: 0.2,
      }).addTo(map);
      const size = map.getSize();
      const bounds = map.getBounds();
      const sw = L.CRS.EPSG3857.project(bounds.getSouthWest());
      const ne = L.CRS.EPSG3857.project(bounds.getNorthEast());
      const url = featureInfoUrl(p.wms_url, {
        layers: queryable,
        bbox: [sw.x, sw.y, ne.x, ne.y],
        width: size.x,
        height: size.y,
        x: Math.round(e.containerPoint.x),
        y: Math.round(e.containerPoint.y),
      });
      const seq = ++identifySeq.current;
      setIdentify({ loading: true, failed: false, hits: [] });
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`GetFeatureInfo ${resp.status}`);
        const hits = parseFeatureInfo(await resp.json(), p.layers);
        if (seq === identifySeq.current) setIdentify({ loading: false, failed: false, hits });
      } catch {
        if (seq === identifySeq.current) setIdentify({ loading: false, failed: true, hits: [] });
      }
    });

    // The portal mounts before the flex layout settles.
    const t = window.setTimeout(() => map.invalidateSize(), 0);
    return () => {
      window.clearTimeout(t);
      map.remove();
      mapRef.current = null;
      baseRef.current = null;
      wmsRef.current = null;
      markerRef.current = null;
    };
  }, []);

  useEffect(() => {
    fitToExtent();
  }, [fitToExtent]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    baseRef.current?.remove();
    baseRef.current = null;
    if (basemap === "osm") {
      baseRef.current = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxNativeZoom: 19,
        maxZoom: 22,
        zIndex: 1,
        attribution: "© OpenStreetMap",
      }).addTo(map);
    } else if (basemap === "orto") {
      baseRef.current = L.tileLayer
        .wms(ORTO_WMS, {
          layers: "Raster",
          format: "image/png",
          version: "1.3.0",
          tileSize: 512,
          maxZoom: 22,
          zIndex: 1,
          attribution: "© GUGiK",
        })
        .addTo(map);
    }
  }, [basemap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!visibleNames) {
      wmsRef.current?.remove();
      wmsRef.current = null;
      wmsLayersRef.current = "";
      setTilesLoading(false);
      return;
    }
    if (!wmsRef.current) {
      const wms = L.tileLayer.wms(preview.wms_url, {
        layers: visibleNames,
        format: "image/png",
        transparent: true,
        version: "1.3.0",
        tileSize: 512,
        maxZoom: 22,
        zIndex: 10,
      });
      wms.on("loading", () => setTilesLoading(true));
      wms.on("load", () => setTilesLoading(false));
      wms.addTo(map);
      wmsRef.current = wms;
    } else if (visibleNames !== wmsLayersRef.current) {
      wmsRef.current.setUrl(preview.wms_url, true);
      wmsRef.current.setParams({ layers: visibleNames } as L.WMSParams);
    } else {
      // Only the signature was refreshed: new tiles use it, the drawn ones stay.
      wmsRef.current.setUrl(preview.wms_url, true);
    }
    wmsLayersRef.current = visibleNames;
  }, [preview.wms_url, visibleNames]);

  function toggle(layerName: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(layerName)) next.delete(layerName);
      else next.add(layerName);
      return next;
    });
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col md:flex-row">
      <aside className="shrink-0 max-h-56 md:max-h-none md:w-72 overflow-y-auto space-y-4 border-b md:border-b-0 md:border-r border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3 text-sm">
        <section>
          <h3 className="mb-1.5 text-[11px] font-mono uppercase text-[var(--color-text-muted)]">
            Warstwy
          </h3>
          {/* Listed top → bottom, the way they lie on the map. */}
          <ul className="space-y-1" data-testid="gis-preview-layers">
            {[...preview.layers].reverse().map((l) => (
              <li key={l.name}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!hidden.has(l.name)}
                    onChange={() => toggle(l.name)}
                    className="cursor-pointer accent-[var(--color-primary)]"
                  />
                  <span className="text-[var(--color-text-muted)]" title={KIND[l.kind].label}>
                    {KIND[l.kind].icon}
                  </span>
                  <span className="truncate" title={l.title}>
                    {l.title}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </section>

        {preview.skipped.length > 0 && (
          <section>
            <h3 className="mb-1.5 text-[11px] font-mono uppercase text-[var(--color-text-muted)]">
              Pominięte
            </h3>
            <ul className="space-y-1 text-xs text-[var(--color-text-muted)]">
              {preview.skipped.map((s) => (
                <li key={s.title}>
                  <span className="text-[var(--color-text-primary)]">{s.title}</span> — {s.reason}
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <h3 className="mb-1.5 text-[11px] font-mono uppercase text-[var(--color-text-muted)]">
            Podkład
          </h3>
          <div className="inline-flex border border-[var(--color-border)]" role="group">
            {BASEMAPS.map((b) => (
              <button
                key={b.value}
                type="button"
                onClick={() => setBasemap(b.value)}
                aria-pressed={basemap === b.value}
                className={`px-2.5 py-1 text-xs ${
                  basemap === b.value
                    ? "bg-[var(--color-primary)] text-white"
                    : "text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
        </section>

        <Button size="sm" variant="secondary" onClick={fitToExtent}>
          <Maximize size={13} className="mr-1" /> Przybliż do zasięgu
        </Button>

        <p className="text-[11px] leading-snug text-[var(--color-text-muted)]">
          Kliknij obiekt na mapie, aby zobaczyć jego atrybuty. Podgląd jest tylko do odczytu —
          żeby edytować, pobierz plik i otwórz go w QGIS-ie.
        </p>
      </aside>

      <div className="relative flex-1 min-h-0">
        <div ref={mapEl} className="absolute inset-0" data-testid="gis-preview-map" />
        {tilesLoading && (
          <div className="pointer-events-none absolute bottom-2 left-2 z-[1000] flex items-center gap-1.5 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1 text-[11px] text-[var(--color-text-muted)]">
            <Loader2 size={12} className="animate-spin" /> Wczytuję mapę…
          </div>
        )}
        {identify && <FeatureInfoPanel state={identify} onClose={clearIdentify} />}
      </div>
    </div>
  );
}

function FeatureInfoPanel({ state, onClose }: { state: IdentifyState; onClose: () => void }) {
  return (
    <div
      className="absolute top-2 right-2 z-[1000] w-80 max-w-[calc(100%-1rem)] max-h-[70%] overflow-y-auto border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3 text-sm shadow-lg"
      data-testid="gis-feature-info"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-[11px] font-mono uppercase text-[var(--color-text-muted)]">
          Atrybuty obiektu
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="p-0.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
          aria-label="Zamknij atrybuty"
        >
          <X size={14} />
        </button>
      </div>
      {state.loading ? (
        <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)]">
          <Loader2 size={13} className="animate-spin" /> Szukam obiektów…
        </div>
      ) : state.failed ? (
        <p className="text-xs text-red-700 dark:text-red-400">Nie udało się pobrać atrybutów.</p>
      ) : state.hits.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">Brak obiektu w tym miejscu.</p>
      ) : (
        <div className="space-y-3">
          {state.hits.map((hit, i) => (
            <div key={i}>
              <div className="mb-1 font-medium">{hit.layerTitle}</div>
              {hit.attributes.length === 0 ? (
                <p className="text-xs text-[var(--color-text-muted)]">Obiekt bez atrybutów.</p>
              ) : (
                <table className="w-full text-xs">
                  <tbody>
                    {hit.attributes.map(([key, value]) => (
                      <tr key={key} className="align-top">
                        <td className="py-0.5 pr-2 text-[var(--color-text-muted)] whitespace-nowrap">
                          {key}
                        </td>
                        <td className="py-0.5 break-words">{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
